import { Reducer, useCallback, useReducer, useState } from 'react';
import buildCompleteConfig from './config.js';
import reducer from './reducer.js';
import {
  Action,
  FormState,
  GenericFormData,
  UseFormResponse,
  ValidationConfig,
  ValidationMode,
} from './types.js';
import { generateErrors, isValid as isDataValid } from './validate.js';

export default function useForm<FormData extends GenericFormData>(
  initialState: FormData,
  config: ValidationConfig<FormData> = {},
  validationMode = ValidationMode.OnEdit,
): UseFormResponse<FormData> {
  const [{ formData, touchedFields }, dispatch] = useReducer<
    Reducer<FormState<FormData>, Action<FormData>>
  >(reducer, {
    formData: initialState,
    touchedFields: {},
  });
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const validation = buildCompleteConfig(initialState, config);

  const doValidation = (isSubmitted: boolean) => {
    const { errors, counters } = generateErrors(
      { formData, touchedFields },
      validation,
      isSubmitted,
      validationMode,
    );
    const isValid = isDataValid(formData, validation, errors);
    return { errors, isValid, counters };
  };

  const { errors, isValid, counters } = doValidation(hasSubmitted);

  const trySubmit = (doSubmit: () => void): void => {
    setHasSubmitted(true);
    let check = isValid;
    if (validationMode === ValidationMode.OnSubmit) {
      check = doValidation(true).isValid;
    }
    if (check) doSubmit();
  };

  // TODO: Also enable resetting state here?
  const resetForm = useCallback((): void => setHasSubmitted(false), []);

  return {
    formData,
    dispatch,
    isValid,
    errors,
    trySubmit,
    resetForm,
    touchedFields,
    isPristine: Object.keys(touchedFields).length <= 0,
    counters,
  };
}
